import React, { useState } from 'react';
import { FlatList } from 'react-native';
import type { FocusPositionProps, LocationProps, MyPlayerProps, PlayerAddressProps } from '../../types';
import { WalletApi, WalletHelpers } from '../api';
import AddressCard from './AddressCard';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type AddressManaertProps = {
    player?:MyPlayerProps,
    insets?:{ top:number, bottom:number, right:number, left:number }
    onFocusPosition?:(pos:FocusPositionProps) => void,
    location?: {
        loading: boolean,
        error?:string,
        coordinates:any,
        address_results:any[],
        player_address?:PlayerAddressProps,
        region?:string,
        location?:LocationProps
    }
    player_addresses:PlayerAddressProps[],
    onAddAddress:(address:PlayerAddressProps) => void,
    onRemoveAddress?:(address:PlayerAddressProps) => void,
    onSelect: (address:PlayerAddressProps) => void
    onCancel: () => void
}

const AddressManager = ({ location, insets, player_addresses, onFocusPosition, onSelect, onCancel, onAddAddress, onRemoveAddress }:AddressManaertProps) => {
    const Colors = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ draft_address, setDraftAddress ] = useState<PlayerAddressProps|undefined>(undefined)

    const address_errors = WalletHelpers.isAddressValid(draft_address)

    const handleCreateAddress = async() => {
        if(!draft_address){ return } //
        if(loading){ return } //Prevent button mashing
        if(address_errors.length > 0){ return }
        setLoading(true)
        const new_addy = await WalletApi.createAddress(draft_address)
        onAddAddress(new_addy)
        setLoading(false)
        setDraftAddress(undefined)
    }

    const handleDeleteAddres = async(address:PlayerAddressProps) => {
        if(loading || !onRemoveAddress){ return } //No no button mash
        //if(!selected_address){ return } //No selected address
        setLoading(true)
        const removed_addy = await WalletApi.deleteAddress(address.player_address_id);
        onRemoveAddress(removed_addy)
        setLoading(false)
    }

    const tryAutoFill = async() => {
        if(!draft_address || !location?.player_address){ return }
        setDraftAddress({ ...location.player_address })
    }



    const renderAddresses = (data:{ item:PlayerAddressProps, index:number }) => {
        return (
            <View style={{ margin:3 }}>
                <AddressCard 
                    address={data.item} 
                    hide_delete={onRemoveAddress ? false : true}
                    onSelect={(a) => onSelect(a)}
                    onDelete={async(a) => {
                        let confirmed = await confirm('Are you sure you would like to remove this address?')
                        if(confirmed){ handleDeleteAddres(a) }
                    }}
                    />
            </View>
        )
    }
    if(draft_address){
        return (
            <View style={{ flex:1, minHeight:600, paddingTop:insets?.top ?? 0, paddingBottom:insets?.bottom ?? 0 }}>
                <View style={{ padding:20 }}>
                    <Text size={30} theme='h1'>CREATE NEW ADDRESS</Text>
                    <Text style={{ marginTop:15 }} size={16} theme='description'>Use the form below to create a new address.</Text>
                </View>
                <View style={{ flex:1, padding:20 }}>
                    <View nativeID='address_name' style={{ borderRadius:4, margin:5, padding:10 }}>
                        <Text size={16} theme='h1' textAlign='center'>Address Name / Label</Text>
                        <Text style={{ margin:3 }} size={12} theme='description' textAlign='center'>Give address a name (i.e., Home) to make it easily identifiable.</Text>
                        <TextInput
                            value={draft_address.name}
                            onFocusPosition={onFocusPosition}
                            placeholder='My Home'
                            onChangeText={(e) => setDraftAddress({ ...draft_address, name: e })}
                        />
                        {!draft_address.name ?
                        <Text size={12} color={Colors.text.warning} textAlign='right'>Please give address a name</Text>
                        :<></>}
                        {location?.player_address ?
                        <Button style={{ marginTop:10, padding:10 }} onPress={() => tryAutoFill()}>
                            <Text size={14} textAlign='center' weight='regular' theme='action'>Autofill with Location</Text>
                        </Button>
                        :<></>}
                    </View>
                    <View nativeID='address_street' style={{ flexDirection:'row', alignItems:'center', margin:5 }}>
                        <Text style={{ flex:1/4 }} size={14} theme='description'>Street</Text>
                        <TextInput 
                            style={{ flex:1 }}
                            onFocusPosition={onFocusPosition}
                            value={draft_address.street}
                            onChangeText={(e) => setDraftAddress({ ...draft_address, street: e })}
                        />
                    </View>
                    <View nativeID='address_city' style={{ flexDirection:'row', alignItems:'center', margin:5 }}>
                        <Text style={{ flex:1/4 }} size={14} theme='description'>City</Text>
                        <TextInput 
                            style={{ flex:1 }}
                            onFocusPosition={onFocusPosition}
                            value={draft_address.city}
                            onChangeText={(e) => setDraftAddress({ ...draft_address, city: e })}
                        />
                    </View>
                    <View nativeID='address_state' style={{ flexDirection:'row', alignItems:'center', margin:5 }}>
                        <Text style={{ flex:1/4 }} size={14} theme='description'>State</Text>
                        <TextInput 
                            value={draft_address.state}
                            onFocusPosition={onFocusPosition}
                            placeholder='MN'
                            style={{ flex:1 }}
                            onChangeText={(e) => setDraftAddress({ ...draft_address, state: e })}
                        />
                    </View>
                    {draft_address.state.length != 2 ?
                    <Text size={12} color={Colors.text.warning} textAlign='right'>Two letter codes only accepted</Text>
                    :<></>}
                    <View nativeID='address_zip' style={{ flexDirection:'row', alignItems:'center', margin:5 }}>
                        <Text style={{ flex:1/4 }} size={14} theme='description'>Zip</Text>
                        <TextInput 
                            style={{ flex:1 }}
                            onFocusPosition={onFocusPosition}
                            value={draft_address.zip}
                            onChangeText={(e) => setDraftAddress({ ...draft_address, zip: e })}
                        />
                    </View>
                </View>
                <View style={{ flexDirection:'row', padding:20 }}>
                    <Button
                        style={{ flex:1, marginRight:5, opacity: 1 }}
                        title={'CANCEL'}
                        type='error'
                        onPress={() => setDraftAddress(undefined)}
                    />
                    <Button
                        style={{ flex:3, opacity:address_errors.length == 0 && !loading ? 1 : 0.5 }}
                        title={'CREATE'}
                        disabled={address_errors.length > 0 || loading ? true : false}
                        type='success'
                        onPress={() => handleCreateAddress()}
                    />
                </View>
                
            </View>
        )
    }


    return (
        <View style={{ flex:1, minHeight:600 }}>
            <View style={{ padding:20 }}>
                <Text size={30} theme='h1'>MY ADDRESSES</Text>
                <Text style={{ marginTop:15 }} size={16} theme='description'>Below are the addresses we have on file. Manage or add a new one below</Text>
            </View>
            {player_addresses.length == 0 ?
            <View float style={{ flex:1, padding:10, borderRadius:8, margin:10, justifyContent:'center', alignItems:'center' }}>
                <Text size={18} theme='h1' textAlign='center'>Uh Oh!</Text>
                <Text style={{ marginTop:10 }} size={14} theme='description' textAlign='center'>It looks like you dont have any saved addresses</Text>
                <Button 
                    style={{ marginTop:20, padding:10, borderRadius:4 }} 
                    type='success'
                    title='Create New Address'
                    onPress={() => setDraftAddress(WalletHelpers.getEmptyAddress())}/>
            </View>
            :<></>}
            <View style={{ flex:1, padding:20 }}>
                <FlatList data={player_addresses} renderItem={renderAddresses} />
            </View>
            <View nativeID='action_row' style={{ flexDirection:'row', padding:20 }}>
                <Button
                    style={{ flex:1, marginRight:5, opacity: 1 }}
                    title={'CLOSE'}
                    type='close'
                    onPress={() => onCancel()}
                />
                <Button
                    style={{ flex:3 }}
                    title={'NEW ADDRESS'}
                    type='success'
                    onPress={() => setDraftAddress(WalletHelpers.getEmptyAddress())}
                />
            </View>
        </View>
    )
}

export default AddressManager